import Report from "@mui/icons-material/Report";
import Warning from "@mui/icons-material/Warning";
import Button from "@mui/material/Button";
import type { ButtonProps } from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { getErrorMessage } from "./get-error-message";

export type ConfirmationActionType = {
  label: string;
  type?: "error" | "warning" | "success" | "info";
  variant?: ButtonProps["variant"];
  focused?: boolean;
  onClick?: () => Promise<void> | void;
};

export type ConfirmationDialogType = {
  id?: string;
  open: boolean;
  type?: "info" | "warning" | "danger";
  title?: string | ReactElement;
  content?: string | ReactElement;
  actions: ConfirmationActionType[];
};

export type ConfirmationDialogProps = ConfirmationDialogType & {
  onClose: () => void;
  renderText?: (value: string) => ReactNode;
};

function renderMaybeTranslated(
  value: string | ReactElement | undefined,
  renderText?: (value: string) => ReactNode,
): ReactNode {
  if (typeof value === "string") {
    return renderText ? renderText(value) : value;
  }

  return value;
}

export function ConfirmationDialog({
  id,
  open,
  type,
  title,
  content,
  actions = [],
  onClose,
  renderText,
}: ConfirmationDialogProps) {
  const [error, setError] = useState<string>();
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(undefined);
      setDisabled(false);
    }
  }, [open]);

  return (
    <Dialog
      id={id}
      open={open}
      aria-label={title ? undefined : "Confirmation dialog"}
      aria-labelledby={
        typeof title === "string" ? "confirmation-dialog-title" : undefined
      }
      aria-describedby="confirmation-dialog-description"
      PaperProps={{
        sx: { pt: 1, pb: 2 },
      }}
    >
      {type === "warning" && (
        <div className="my-8 flex items-center justify-center">
          <Warning
            fontSize="large"
            sx={{ color: "var(--color-chart--warning--dark)" }}
          />
          <Typography variant="h3" sx={{ mx: 1, mt: 1 }}>
            {renderText ? renderText("Warning") : "Warning"}
          </Typography>
        </div>
      )}
      {type === "danger" && (
        <div className="mb-8 flex items-center justify-center">
          <Report
            fontSize="large"
            sx={{ color: "var(--color-chart--overdue--dark)" }}
          />
          <Typography variant="h3" sx={{ mx: 1, mt: 1 }}>
            {renderText ? renderText("Danger") : "Danger"}
          </Typography>
        </div>
      )}
      {typeof title === "string" ? (
        <DialogTitle
          sx={{ textAlign: "center", color: "var(--color-text--secondary)" }}
          id="confirmation-dialog-title"
        >
          {renderText ? renderText(title) : title}
        </DialogTitle>
      ) : (
        title
      )}
      <DialogContent>
        <DialogContentText
          component="div"
          style={{ textAlign: "center" }}
          id="confirmation-dialog-description"
        >
          {renderMaybeTranslated(content, renderText)}
        </DialogContentText>
        {error ? (
          <DialogContentText
            component="div"
            color="error"
            sx={{ mt: 3, textAlign: "center" }}
          >
            {error}
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", justifyContent: "center" }}>
        {actions.map((action, key) => (
          <Button
            disabled={disabled}
            key={`${action.label}.${key}`}
            variant={action.variant || "text"}
            onClick={async () => {
              setDisabled(true);
              setError(undefined);

              try {
                await action.onClick?.();
                onClose();
              } catch (err) {
                setError(getErrorMessage(err));
              } finally {
                setDisabled(false);
              }
            }}
            autoFocus={action.focused}
            color={action.type || "primary"}
          >
            {renderText ? renderText(action.label) : action.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
}
